import React from 'react'
import data from './data.json'
import { Intro, Layout, Header, SectionTitle } from '../../../../components'

export default () => {
  return (
    <>
      <Header
        site={data.site}
        slug={data.slug + '/design/'}
        //state="design" disabling switcher
        title={data.title}
      />
      <Layout
        site={data.site}
        slug={data.slug + '/design/'}
        state="design"
        title={data.title}
      >
        <Intro>
          <p>
            Action language can appear within an experience in the form of CTAs
            (Calls to Action), user prompts and feedback messages, as described
            below.
          </p>
        </Intro>
        <SectionTitle>CTA</SectionTitle>
        <p>
          Calls to action (CTAs) are most often used in buttons, but can also be
          found in titles and links, depending on the interaction. Note that CTA
          usage always adheres to the M{'&'}T Bank brand tenets for voice and
          tone, specifically that we are always approachable, clear, helpful and
          conversational with users.
        </p>

        <table className="table">
          <thead>
            <th width="20%">CTA Type</th>
            <th>Description</th>
            <th>Examples</th>
          </thead>
          <tbody>
            <tr>
              <td>Short</td>
              <td className="u-type--small u-type--normal">
                A grouping of three or four words, CTA Short is usually found in
                headlines, subheads, section header titles and links. These are
                best when asking the user to complete a single action.
              </td>
              <td className="u-type--small u-type--normal" width="30%">
                <ul className="l-padding--none">
                  <li>Choose a debit card</li>
                  <li>Create your PIN</li>
                  <li>Make a deposit</li>
                </ul>
              </td>
            </tr>
            <tr>
              <td>Long</td>
              <td className="u-type--small u-type--normal">
                CTA Long is usually a short sentence, and functions similarly to
                CTA Short, so can be found in similar locations. These are best
                when asking the user to complete a series of actions in service
                of a single goal.
              </td>
              <td className="u-type--small u-type--normal">
                <ul className="l-padding--none">
                  <li>Now, let’s continue with the second applicant</li>
                  <li>Let’s answer some account activity questions.</li>
                  <li>Set up your Online Banking profile.</li>
                </ul>
              </td>
            </tr>
            <tr>
              <td>Button</td>
              <td className="u-type--small u-type--normal">
                These are short, ideally single-word touts, with a maximum of
                four words. A button CTA should act as a bridge to the next page
                so that users know exactly where they are headed.
              </td>
              <td className="u-type--small u-type--normal">
                <ul className="l-padding--none">
                  <li>Continue</li>
                  <li>Accept</li>
                  <li>Apply Now</li>
                  <li>Open Account</li>
                </ul>
              </td>
            </tr>
          </tbody>
        </table>
        <SectionTitle>User Prompts</SectionTitle>
        <p>
          Typically found in subheads and form entry titles, these prompts
          direct the user to input, download, or confirm information. They can
          take the form of commands or questions, and the user will respond by
          interacting with the associated button, link or form input components.
          When writing questions, adhere to M{'&'}T Bank brand tenets and focus
          the “asks” on what’s important in order to move the user efficiently
          through the flow. Confirmation questions tend to be binary, asked when
          there is a specific need for a “yes” or “no” answer.
        </p>
        <ul className="voice-tone__tone">
          <h3 className="c-title-3--normal l-margin-bottom--medium">
            Examples
          </h3>
          <li>
            For your security, please verify your identity by answering the
            following questions.
          </li>
          <li>
            Please open, read and save the agreements below prior to acceptance.
          </li>
          <li>Select those that apply:</li>
          <li>Would you like to order checks now?</li>
          <li>How many times per month will you withdraw or deposit cash?</li>
        </ul>

        <p>
          When appearing in standalone checkbox components, user prompts are
          written in the first person from the user's perspective. Write in the
          active voice with positive verbs so the user can be sure what they are
          agreeing to.
        </p>
        <h3 className="c-title-3--normal l-margin-bottom--medium">Examples</h3>
        <div class="c-checkbox__container">
          <div>
            <input class="c-checkbox__input" type="checkbox" id="checkbox3" />
            <label class="c-checkbox" htmlFor="checkbox3">
              By checking this box, I am electing to receive my statement and
              notices online.
              <span class="c-checkbox__checkmark">
                <svg
                  class="c-checkbox__checkmark-icon"
                  version="1.1"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 32 32"
                  fill="#fff"
                >
                  <path d="M27 4l-15 15-7-7-5 5 12 12 20-20z"></path>
                </svg>
              </span>
            </label>
          </div>
        </div>

        <hr />
        <SectionTitle>Feedback and Error Messages</SectionTitle>
        <p>
          Warning and error messages will direct the user to take action to
          prevent an unwanted outcome. This will be different from the primary
          CTA on the screen, so use the appropriate presentation of the Feedback
          and Error Message to be specific about what needs to be done. If the
          action to be taken is located on a different page, include a link to
          that page.
        </p>
        <h3 className="c-title-3--normal l-margin-bottom--medium">Examples</h3>
        <div class="c-notification--inline-error">
          <div class="c-notification__icon">
            <i class="icon icon--error"></i>
          </div>
          <div class="c-notification__content">
            Your account has been opened, but your card transaction has been
            declined. Please deposit at least $25.00 in a branch or online
            within 60 days to keep your account open.
          </div>
        </div>

        <div class="c-notification--inline-warning l-margin-top--large">
          <div class="c-notification__icon">
            <i class="icon icon--error"></i>
          </div>
          <div class="c-notification__content">
            You'll need to visit a branch to open a joint account.{' '}
            <a
              className="link"
              href="https://locations.mtb.com/?ch=psg&cid=psg|2020brand|443567945210|google|28238075849|aud-425830411114:kwd-172559262&matchtype=p&adid=443567945210&addisttype=g&kxconfid=ug485menz&campaign=423329009&campaignid=psg&siteid=google&gclid=Cj0KCQjwjer4BRCZARIsABK4QeU_Y3nt8LJInQC6l1waSrjiXAazpPILgwtzMfwCiHYP5Xu0NywgRDAaAqFGEALw_wcB"
            >
              Find an M{'&'}T branch near you.
            </a>
          </div>
        </div>
      </Layout>
    </>
  )
}
